import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import {
  PRODUCTS_QUERY,
  storefrontApiRequest,
  type ShopifyProduct,
} from "@/lib/shopify";
import { trackRailInteraction } from "@/lib/gtag";

const trendingProductsQueryOptions = {
  queryKey: ["products", "trending-now"] as const,
  queryFn: async () => {
    try {
      const res = await storefrontApiRequest(PRODUCTS_QUERY, {
        first: 16,
        query:
          "tag:suosittu OR tag:bestseller OR title:oppimistorni OR title:pikler OR title:iglu",
      });
      return (res?.data?.products?.edges ?? []) as ShopifyProduct[];
    } catch (err) {
      console.error("[home] trending rail fetch failed:", err);
      return [] as ShopifyProduct[];
    }
  },
  staleTime: 60_000,
};

const hasGoodImage = (p: ShopifyProduct) => !!p.node.images.edges[0]?.node.url;

/**
 * "Suosituimmat juuri nyt" — curated bestseller rail. Lazy-loaded from the
 * homepage so its JS + product fetch stay off the critical path on slow
 * mobile connections. Renders a skeleton while the chunk + data are in
 * flight.
 */
export default function TrendingNow() {
  const q = useQuery(trendingProductsQueryOptions);
  const scrollerRef = useRef<HTMLDivElement>(null);

  if (q.isLoading) return <TrendingSkeleton />;
  const products = (q.data ?? []).filter(hasGoodImage).slice(0, 8);
  if (products.length === 0) return null;

  const railKey = "suosituimmat juuri nyt";
  const scrollBy = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: "smooth" });
    trackRailInteraction({ rail: railKey, action: "scroll" });
  };

  return (
    <section className="border-t border-border bg-card">
      <div className="container-prose section-y">
        <div className="flex items-end justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-clay">
              Vanhempien valinta
            </p>
            <h2 className="mt-2 text-3xl text-foreground md:text-5xl text-balance">
              Suosituimmat juuri nyt.
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-1.5">
              <button
                type="button"
                aria-label="Edellinen"
                onClick={() => scrollBy(-1)}
                className="grid h-9 w-9 place-items-center rounded-full border border-border bg-background text-foreground transition hover:bg-foreground hover:text-background"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Seuraava"
                onClick={() => scrollBy(1)}
                className="grid h-9 w-9 place-items-center rounded-full border border-border bg-background text-foreground transition hover:bg-foreground hover:text-background"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <Link
              to="/kauppa"
              className="text-sm font-medium text-foreground underline-offset-4 hover:underline"
              onClick={() =>
                trackRailInteraction({ rail: railKey, action: "view_all" })
              }
            >
              Kaikki →
            </Link>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="mt-8 md:mt-12 -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {products.map((p, i) => (
            <div
              key={p.node.id}
              className="w-[78%] shrink-0 snap-start sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)]"
            >
              <ProductCard
                product={p}
                showAddToCart={false}
                sizes="(min-width: 1280px) 360px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 78vw"
                railName={railKey}
                railPosition={i}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Static skeleton shown while the TrendingNow chunk + Shopify query resolve.
 * Matches the real rail's geometry so there's no layout shift on hydrate.
 */
export function TrendingSkeleton() {
  return (
    <section
      aria-hidden
      className="border-t border-border bg-card"
      data-testid="trending-skeleton"
    >
      <div className="container-prose section-y">
        <div className="flex items-end justify-between gap-6">
          <div className="w-full max-w-xs">
            <div className="h-3 w-32 rounded-full bg-muted animate-pulse" />
            <div className="mt-3 h-8 w-64 rounded-md bg-muted animate-pulse md:h-10" />
          </div>
          <div className="hidden md:flex items-center gap-1.5">
            <div className="h-9 w-9 rounded-full bg-muted animate-pulse" />
            <div className="h-9 w-9 rounded-full bg-muted animate-pulse" />
          </div>
        </div>
        <div className="mt-8 md:mt-12 -mx-4 flex gap-4 overflow-hidden px-4 pb-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="w-[78%] shrink-0 sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)]"
            >
              <div className="aspect-square w-full rounded-2xl bg-muted animate-pulse" />
              <div className="mt-3 h-4 w-3/4 rounded-md bg-muted animate-pulse" />
              <div className="mt-2 h-4 w-1/3 rounded-md bg-muted animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}